/**
 * Arrivals, as props for an `m` component: `<m.li {...riseIn(index)} />`. Under reduced motion the provider completes
 * them at once. The Panel polls: give a row a stable `key`, so a refresh that changes nothing replays nothing.
 */

import { SPRING } from './motion-provider';

export interface RiseInOptions {
  /** Seconds between one item and the next. */
  readonly step?: number;
  /** Items after this one arrive together with it. */
  readonly max?: number;
  /** Pixels it rises. */
  readonly y?: number;
  readonly scale?: number;
}

/* Motion hands an opacity to the browser, which starts it a frame before the script moves `y` and `scale`: the fade
   would lead the rise. An update listener keeps every value of the item on the script's clock, so the item fades and
   rises together, as in the mockups. (The movement stays `y` and `scale`, not one `transform`, because an item may also
   carry `layout`, which writes the transform itself.) */
const together = (): void => undefined;

/** A group arrives one after another: each item rises and fades in on the snap spring. */
export function riseIn(index = 0, { step = 0.028, max = 14, y = 16, scale = 0.97 }: RiseInOptions = {}) {
  return {
    initial: { opacity: 0, y, scale },
    animate: { opacity: 1, y: 0, scale: 1 },
    transition: { ...SPRING.snap, delay: Math.min(index, max) * step },
    onUpdate: together,
  } as const;
}

/** A line that draws itself (the tick of a charge): for an `m.path`. */
export function drawIn(delay = 0.15) {
  return {
    initial: { pathLength: 0, opacity: 0 },
    animate: { pathLength: 1, opacity: 1 },
    transition: { duration: 0.5, delay, ease: [0.3, 0.7, 0.2, 1] },
  } as const;
}
